import { Card, FormControl, InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid2";
import cep from "cep-promise";
import { useFormikContext } from "formik";
import React, { useRef } from "react";
import InputMaskWrapper from "@/components/atoms/InputMaskWrapper";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import MDTypography from "@/components/atoms/MDTypography";
import { CEP_LENGHT, STATE_UF_TRANSLATIONS } from "@/constants/documents.constants";
import { removeNonIntegersFromString } from "@/helpers/string-helpers";
import useLoading from "@/hooks/use-loading";
import { ContactAddressMessages } from "@/messages/contacts/address-section.messages";

import { ContactFormData } from "../../../../types";

type Props = {
  messages: ContactAddressMessages;
};

export default function AddressSection({ messages }: Props) {
  const formik = useFormikContext<ContactFormData>();
  const { values, handleChange, errors, setFieldValue, setFieldError } = formik;

  const {
    startLoading,
    endLoading,
    isLoading: isLoadingCEP,
    loadingIcon: loadingIconCEP,
  } = useLoading({ loadingIconColor: "primary" });

  const addressNumberRef = useRef<HTMLInputElement>(null);

  const clearData = () => {
    setFieldValue("addressState", "");
    setFieldValue("addressStateUf", "");
    setFieldValue("addressCity", "");
    setFieldValue("addressDistrict", "");
    setFieldValue("address", "");
  };

  const handleCEPChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const {
      target: { value },
    } = e;
    const formattedValue = removeNonIntegersFromString(value);
    setFieldError("addressZipCode", undefined);
    setFieldValue("addressZipCode", formattedValue);
    const isCEPCompleted = formattedValue.length === CEP_LENGHT;
    if (!isCEPCompleted) clearData();
    if (isCEPCompleted) {
      startLoading();
      try {
        const cepData = await cep(formattedValue);
        const addressState = STATE_UF_TRANSLATIONS[cepData.state];
        setFieldValue("addressState", addressState);
        setFieldValue("addressStateUf", cepData.state);
        setFieldValue("addressCity", cepData.city);
        setFieldValue("addressDistrict", cepData.neighborhood);
        setFieldValue("address", cepData.street);

        setTimeout(() => {
          addressNumberRef.current?.focus();
        }, 0);
      } catch (err) {
        setFieldError("addressZipCode", messages.cepNotFoundError);
        clearData();
      }
      endLoading();
    }
  };

  return (
    <Card>
      <MDBox p={3}>
        <MDBox display="flex" justifyContent="space-between">
          <MDTypography variant="h5" fontWeight="bold">
            {messages.contactAddressTitle}
          </MDTypography>
        </MDBox>

        <MDBox mt={1}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, sm: 3 }}>
              <InputMaskWrapper
                mask={"99999-999"}
                value={values.addressZipCode}
                name="addressZipCode"
                onChange={(e) => handleCEPChange(e)}
                variant="standard"
                label={messages.addressZipCodeLabel}
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                error={!!errors.addressZipCode}
                helperText={errors.addressZipCode}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <FormField
                variant="standard"
                name="address"
                label={messages.addressLabel}
                value={values.address}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.address}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <FormField
                variant="standard"
                name="addressNumber"
                label={messages.addressNumberLabel}
                value={values.addressNumber}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.addressNumber}
                inputRef={addressNumberRef}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <FormField
                variant="standard"
                name="addressCompl"
                label={messages.addressComplLabel}
                value={values.addressCompl}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.addressCompl}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <FormField
                variant="standard"
                name="addressDistrict"
                label={messages.addressDistrictLabel}
                value={values.addressDistrict}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.addressDistrict}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <FormField
                variant="standard"
                name="addressCity"
                label={messages.addressCityLabel}
                value={values.addressCity}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.addressCity}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <FormControl fullWidth error={!!errors.addressStateUf}>
                <FormField
                  variant="standard"
                  label={messages.addressStateLabel}
                  name="addressStateUf"
                  value={values.addressStateUf}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.addressStateUf}
                />
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, sm: 3 }}>
              <FormField
                variant="standard"
                name="addressCountry"
                label={messages.addressCountryLabel}
                value={values.addressCountry}
                onChange={handleChange}
                fullWidth
                InputLabelProps={{ shrink: true }}
                error={!!errors.addressCountry}
              />
            </Grid>
          </Grid>
        </MDBox>
      </MDBox>
    </Card>
  );
}
